import {
    BookerAttendeeBookingInput,
    BookerAttendeeInput,
    GetCourseDetailsByIdPublicQuery,
} from "../../generated/graphql"

const attendeeIsBookedIntoPreRequisiteCourse = (
    attendee: BookerAttendeeInput,
    isCourseWithPreRequisites: boolean,
    courseId: string,
    bookingRestriction: GetCourseDetailsByIdPublicQuery["courseByIdPublic"]["bookingRestriction"],
    includeWaitingList: boolean,
    includeBookingList: boolean
) => {
    const prerequisiteCourses = bookingRestriction?.prerequisiteCourses || []
    if (!isCourseWithPreRequisites) return false
    if (!attendee.bookings) return false

    return attendee.bookings.some((booking: BookerAttendeeBookingInput) => {
        const isPrerequisite = prerequisiteCourses.some(
            (prerequisiteCourse) => prerequisiteCourse.id === booking.courseId
        )
        if (!isPrerequisite) return false

        const isOnWaitingList = booking.bookingNumberPrefix === "W" // Assuming there's a flag to check if booking is on the waiting list
        const isBooked = booking.bookingNumberPrefix === "B" // Assuming there's a flag to check if booking is confirmed

        return (includeWaitingList && isOnWaitingList) || (includeBookingList && isBooked)
    })
}

// Stornierte Buchungen blockieren keine Neubuchung. Lokaler Helfer-Type, weil
// `BookerAttendeeBookingInput` (GraphQL InputType) das Feld `canceled` nicht kennt;
// per Query (siehe booker.graphql.ts) wird es zur Laufzeit aber mitgeliefert.
type BookingWithCancelStatus = { courseId?: string | null; canceled?: string | Date | null }

const attendeeIsAllreadyBookedIntoCourse = (attendee: BookerAttendeeInput, courseId: string) => {
    if (!attendee.bookings) return false
    return attendee.bookings.some(
        (booking: BookerAttendeeBookingInput) =>
            booking.courseId === courseId && !(booking as BookingWithCancelStatus).canceled
    )
}

const attendeeIsAMember = (attendee: BookerAttendeeInput) => {
    return attendee.member
}

type CourseWithBookingRestriction<R> = { allowLessonBooking?: boolean | null; bookingRestriction?: R | null }

/**
 * Buchungsbeschränkung, wie der Server sie für diesen Kurs anwendet: Einzelbuchungskurse
 * (`allowLessonBooking`) prüfen ohne Wartelisten-Fallback (`createLessonBooking`), dort bleibt
 * ein Teilnehmer ohne Voraussetzung gesperrt. Alle Prüfungen und Hinweise lesen die
 * Beschränkung über diesen Helfer, damit die EUA keinen unmöglichen Wartelisteneintrag anbietet.
 */
export function effectiveBookingRestriction<R extends { allowWaitlistWithoutPrerequisite?: boolean | null }>(
    course: CourseWithBookingRestriction<R> | null | undefined
): R | null | undefined {
    const restriction = course?.bookingRestriction
    if (restriction && course?.allowLessonBooking && restriction.allowWaitlistWithoutPrerequisite) {
        return { ...restriction, allowWaitlistWithoutPrerequisite: false }
    }
    return restriction
}

type AttendeeCheckType = {
    id: string
    firstName: string
    isMember: boolean
    allreadyBooked: boolean
    bookedIntoPreRequisite: boolean
    /**
     * Voraussetzung fehlt, aber die Buchungsbeschränkung erlaubt dann die Warteliste
     * (`allowWaitlistWithoutPrerequisite`). Der Teilnehmer ist wählbar, die Buchung
     * wird zum Wartelisteneintrag (der Server entscheidet genauso). Bei Einzelbuchungskursen
     * gilt das nicht; Aufrufer übergeben deshalb `effectiveBookingRestriction(course)`.
     */
    waitlistOnly: boolean
}

export const attendeeCheck = (
    attendee: BookerAttendeeInput,
    courseId?: string,
    isCourseWithPreRequisites?: boolean,
    bookingRestriction?: GetCourseDetailsByIdPublicQuery["courseByIdPublic"]["bookingRestriction"]
): AttendeeCheckType => {
    const includeWaitingList = bookingRestriction?.includeWaitingList || false
    const includeBookingList = bookingRestriction?.includeBookingList || false
    const allreadyBooked = !courseId ? false : attendeeIsAllreadyBookedIntoCourse(attendee, courseId)
    const bookedIntoPreRequisite =
        !courseId || !isCourseWithPreRequisites
            ? false
            : attendeeIsBookedIntoPreRequisiteCourse(
                  attendee,
                  isCourseWithPreRequisites,
                  courseId,
                  bookingRestriction,
                  includeWaitingList,
                  includeBookingList
              )
    const attendeeIsMember = attendeeIsAMember(attendee)

    const waitlistOnly = Boolean(
        courseId && isCourseWithPreRequisites && !bookedIntoPreRequisite && bookingRestriction?.allowWaitlistWithoutPrerequisite
    )

    return {
        id: attendee.id,
        firstName: attendee.firstname,
        allreadyBooked,
        bookedIntoPreRequisite,
        isMember: attendeeIsMember,
        waitlistOnly,
    }
}

/**
 * Darf dieser Teilnehmer in den Kurs gebucht werden? Mit `allowWaitlistWithoutPrerequisite`
 * ist ein Teilnehmer ohne Voraussetzung wählbar, aber nur für die Warteliste
 * (`attendeeCheck(...).waitlistOnly`). Fasst die drei
 * auswahlunabhängigen Ausschlussgründe zusammen (bereits gebucht, fehlende
 * Mitgliedschaft, fehlende Voraussetzungs-Buchung). Das Alter hängt an einer
 * Query, die nur für den ausgewählten Teilnehmer läuft, und bleibt daher außen vor.
 *
 * Bewusst gemeinsam genutzt von der Teilnehmer-Vorauswahl (SummaryLeft) und der
 * Entscheidung, ob ein Selbstbucher-Kurs die Auswahl überhaupt anzeigen muss
 * (BookingSummary) — liefen beide auseinander, könnte die Auswahl ausbleiben,
 * obwohl der automatisch gewählte Teilnehmer gar nicht buchbar ist.
 */
export const isAttendeeSelectableForCourse = ({
    attendee,
    courseId,
    isCourseWithPreRequisites,
    bookingRestriction,
    isMemberOnlyCourse,
}: {
    attendee: BookerAttendeeInput
    courseId?: string
    isCourseWithPreRequisites?: boolean
    bookingRestriction?: GetCourseDetailsByIdPublicQuery["courseByIdPublic"]["bookingRestriction"]
    isMemberOnlyCourse?: boolean
}): boolean => {
    const checked = attendeeCheck(attendee, courseId, isCourseWithPreRequisites, bookingRestriction)

    if (checked.allreadyBooked) return false
    if (isMemberOnlyCourse && !checked.isMember) return false
    if (isCourseWithPreRequisites && !checked.bookedIntoPreRequisite && !checked.waitlistOnly) return false

    return true
}
